/*
 * Copyright (c) 2025 AirAsia Berhad (Malaysia). All rights reserved.
 *
 * This software is proprietary and confidential. Unauthorized copying of this file,
 * via any medium, is strictly prohibited. Do not distribute, copy, modify, or reuse
 * any part of this code without explicit written permission from AirAsia Move.
 *
 * For internal use only.
 */

import { Platform } from 'react-native';

export const safeAreaEdges: ('top' | 'bottom' | 'left' | 'right')[] =
  Platform.OS === 'ios'
    ? ['bottom', 'left', 'right']
    : ['top', 'bottom', 'left', 'right'];

// Calendar configurable constants
export const MAX_LOS = 30;
export const MIN_DATE_OFFSET_DAYS = 0;
export const MIN_RETURN_DATE_OFFSET_DAYS = 1;
export const MAX_DATE_OFFSET_DAYS = 365;
export const DEFAULT_DEPARTURE_OFFSET_DAYS = 7;
export const DEFAULT_RETURN_OFFSET_DAYS = 9;
export const DEFAULT_STAY_DAYS = 2;
export const DEFAULT_ONE_WAY_STAY_DAYS = 1;

// Domain constants
export const SNAP_DOMAIN = 'snap';
export const SNAP_DESTINATIONS = 'destinations';
export const SNAP_RECENT_SEARCHES = 'recent_searches';

// Configuration
export const MAX_RECENT_SEARCHES = 3;
export const MAX_RECENT_ORIGIN_SUGGESTIONS = 3;

// Storage keys
export const RECENT_SEARCHES_STORAGE_KEY = '@snap/recent_searches';

// Supported airline profiles for SNAP (dotrez inventory)
export const SNAP_SUPPORTED_PROFILES = ['dotrez'];

export const STATION = {
  ORIGIN: 'origin',
  DESTINATION: 'destination',
} as const;

export const STATION_SELECTION_SOURCE = {
  RECENT: 'recent.fs.list',
  GENERAL: 'gnral',
  NEAREST: 'nearest.airpt',
} as const;

export const SNAP_SEARCH_WIDGET_TEST_IDS = {
  ROOT: 'snap-search-widget',
  SKELETON: 'snap-widget-skeleton',
} as const;
// Config for Station Search
export const DROP_DOWN_PORTAL_HOST_NAME = 'hotels-web-portal-host';

// Search configuration
export const DEFAULT_GEO_ID = 'MY';
export const DEFAULT_CURRENCY = 'MYR';
export const DEFAULT_LANGUAGE = 'en-gb';
export const SNAP_HOTELS_BASE_PATH = '/v2/snap/hotels';
export const SNAP_HOTELS_LIST_PATH = `${SNAP_HOTELS_BASE_PATH}/list`;
export const SNAP_LIST_PAGE_BASE_URLS = {
  prod: `https://www.airasia.com${SNAP_HOTELS_LIST_PATH}`,
  stg: `https://www.stgairasia.com${SNAP_HOTELS_LIST_PATH}`,
} as const;

export const SNAP_LIST_PAGE_BASE_URL = SNAP_LIST_PAGE_BASE_URLS.prod; // fallback
export const SNAP_CHINA_DOMAIN = 'airasia.cn';
export const SNAP_CHINA_LIST_REDIRECT_ROUTE = '/v2/snaplist';

// Route paths SNAP hands to the embedded hotel details page, so the reused
// hotel navigation lands back on SNAP screens instead of the hotel journey.
/**
 * Trip types the SNAP listing API accepts, and the inbound aliases that map
 * onto them. Callers reach us with the value the originating surface used
 * (deeplinks, legacy v5 URLs, the search form), so normalise on the way in
 * rather than comparing strings at the call site.
 */
export const SNAP_TRIP_TYPE = {
  ONE_WAY: 'O',
  ROUND_TRIP: 'R',
} as const;

export type SnapTripType = (typeof SNAP_TRIP_TYPE)[keyof typeof SNAP_TRIP_TYPE];

export const SNAP_TRIP_TYPE_ALIASES: Readonly<Record<string, SnapTripType>> = {
  O: SNAP_TRIP_TYPE.ONE_WAY,
  ONEWAY: SNAP_TRIP_TYPE.ONE_WAY,
  ONE_WAY: SNAP_TRIP_TYPE.ONE_WAY,
  'ONE-WAY': SNAP_TRIP_TYPE.ONE_WAY,
  R: SNAP_TRIP_TYPE.ROUND_TRIP,
  ROUNDTRIP: SNAP_TRIP_TYPE.ROUND_TRIP,
};

/**
 * Matches hotels details `safeAreaEdges`.
 * iOS omits top because the screen SafeAreaView already clears the notch.
 * Android includes top because react-native SafeAreaView does not inset there.
 */
export const SNAP_HOTEL_DETAILS_SAFE_AREA_EDGES: (
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
)[] = Platform.OS === 'ios' ? ['left', 'right'] : ['top', 'left', 'right'];

/**
 * Edges for the page-level safe area inside the screen SafeAreaView.
 * Web applies `env(safe-area-inset-*)` on the screen wrapper, so the inner
 * view adds no further insets. Native keeps the hotels details edges.
 */
export const SNAP_HOTEL_DETAILS_PAGE_SAFE_AREA_EDGES: (
  | 'top'
  | 'bottom'
  | 'left'
  | 'right'
)[] = Platform.OS === 'web' ? [] : SNAP_HOTEL_DETAILS_SAFE_AREA_EDGES;

export const SNAP_HOTEL_ROUTE_PATHS = {
  root: '/',
  results: '/snaplist',
  details: '/details',
  confirmation: '/v2/snap/confirmation',
  mapview: '/mapview',
  tripDetails: '/trip-details',
  payment: '/payment',
  contactDetailForm: '/trip-details/contact-detail-form',
  guestEditForm: '/trip-details/guest-edit-form',
} as const;

export const SNAP_DEFAULT_AIRLINE_PROFILE = 'dotRez';

// Change-flight flow
/** Route of each change-flight page, relative to the SNAP mount. */
export const SNAP_FLIGHT_LISTING_ROUTE_PATHS = {
  depart: '/flights',
  return: '/flights/return',
} as const;

/**
 * The return page carries the listing's query untouched (so its listing query
 * stays a cache hit) plus the departing journey picked on the previous page.
 */
export const SNAP_SELECTED_DEPART_FLIGHT_PARAM = 'selectedDepartFlightId';
/** Scroll offset past which the change-flight list offers Back to top. */
export const SNAP_FLIGHT_LISTING_BACK_TO_TOP_OFFSET = 400;
/** Marks an alternative journey's price as a difference to the current one. */
export const SNAP_FLIGHT_DELTA_PRICE_PREFIX = '+ ';
/** Same, for an alternative that is cheaper than the current journey. */
export const SNAP_FLIGHT_NEGATIVE_DELTA_PRICE_PREFIX = '- ';
/** Airline name shown for any dotRez journey, whatever its carrier code. */
export const SNAP_DOTREZ_AIRLINE_NAME = 'AirAsia';

// Banner configuration
export const SNAP_BANNER_DEFAULT_KEY = 'default';
export const SNAP_BANNER_HEIGHT = 148;
export const SNAP_BANNER_AUTO_SCROLL_INTERVAL = 3000;

// Recent Searches defaults (Scenario 5: expired dates fallback)
export const DEFAULT_RECENT_SEARCH_ADULTS = 2;
export const DEFAULT_RECENT_SEARCH_ROOMS = 1;

// LFC (Low Fare Calendar) constants
export const SNAP_LFC = 'lfc';
export const SNAP_LFC_AIRLINE_PROFILE = 'dotRez';
export const MIN_STATION_CODE_LENGTH = 3;
/**
 * Filler for the calendar's price slot. Snap shows no fares on the calendar,
 * but Phoenix's DateView centres the date number over whatever occupies that
 * slot: a cell rendering '' gets a zero-height line while one rendering NBSP
 * gets a full one, so mixing the two misaligns the date numbers within a row.
 * A non-breaking space keeps every cell identical and invisible.
 * Flights uses '...' instead because it actually displays fares.
 */
export const SNAP_CALENDAR_PRICE_PLACEHOLDER = '\u00A0';

/**
 * Origin stations whose destination-suggestion lookup must be served by a
 * different station's file. Only affects the `/destination/<code>/file.json`
 * request — the user's selected origin stays untouched everywhere else.
 * BKK (Suvarnabhumi) has no AirAsia destination file; DMK (Don Mueang) does.
 */
export const DESTINATION_LOOKUP_STATION_OVERRIDES: Record<string, string> = {
  BKK: 'DMK',
};

export const SNAP_LFC_DIRECTIONS = {
  DEPART: 'depart',
  RETURN: 'return',
} as const;

export const SNAP_USER_TYPE = {
  ANONYMOUS: 'anonymous',
  MEMBER: 'member',
} as const;

export type SnapUserType = (typeof SNAP_USER_TYPE)[keyof typeof SNAP_USER_TYPE];

export const SNAP_SEARCH_WIDGET_TRACKING = {
  ACTIONS: {
    TAP: 'tap',
    ERROR: 'error',
    CLICK: 'click',
  },
  CATEGORY: 'SNAP|snapsearchwidget',
  LINE_OF_BUSINESS: 'SNAP',
  SCREEN_NAME: 'Home',
  VALUE_SEPARATOR: '|',
  TRACK_EVENT_NO_ECOMMERCE: 'trackEventNoEcommerce',
};

export const SNAP_SEARCH_WIDGET_LABELS = {
  NO_RESULTS: 'noresults',
} as const;
// Guest field configuration for SNAP
export const SNAP_GUEST_FIELD_CONFIG = {
  MAX_TOTAL_GUESTS: 9,
  MAX_ADULTS_PER_ROOM: 9,
  MIN_ADULTS_PER_ROOM: 1,
  MAX_CHILDREN_PER_ROOM: 8,
  MIN_CHILDREN_PER_ROOM: 0,
  MAX_INFANTS_PER_ROOM: 4,
  MIN_INFANTS_PER_ROOM: 0,
  MAX_CHILD_AGE: 11,
  MIN_CHILD_AGE: 2,
  DEFAULT_CHILD_AGE: 1,
  WEB_MAX_HEIGHT: 520,
  MAX_ROOMS: 8,
  AGE_OPTIONS: Array.from({ length: 10 }, (_, i) => i + 2),
  DEFAULT_CHILD_AGE_SELECTION: 2,
  GUEST_SHEET_HEIGHT: 480,
  GUEST_SHEET_FOOTER_HEIGHT: 130,
};

// Currencies a SNAP bundle can be paid off in instalments (EMI) for.
export const SNAP_EMI_SUPPORTED_CURRENCIES = new Set(['MYR', 'THB']);
